/**
 * QaTestDetailModal — Shows test instructions, expected result,
 * traffic light voting, and comment input.
 *
 * Shared by QaDashboard and QaSidebar (opens on yellow/red tap).
 */

import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X, ClipboardList, CheckCircle2, MessageSquare } from 'lucide-react';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease-out;
`;

const Modal = styled.div`
  background: #1e1e32;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  width: 90%;
  max-width: 480px;
  max-height: 85vh;
  overflow-y: auto;
  animation: ${slideUp} 0.2s ease-out;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 0;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  padding-right: 12px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  flex-shrink: 0;
  &:hover { color: #fff; background: rgba(255, 255, 255, 0.1); }
`;

const Body = styled.div`
  padding: 16px 20px 20px;
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.$color || '#888'};
  margin-bottom: 6px;

  svg { width: 14px; height: 14px; }
`;

const SectionText = styled.div`
  font-size: 14px;
  color: #e0e0e0;
  line-height: 1.6;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 12px 14px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  margin: 16px 0;
`;

const TrafficRow = styled.div`
  display: flex;
  gap: 8px;
`;

const TrafficBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid;
  transition: all 0.15s;

  background: ${props => props.$active
    ? props.$variant === 'green' ? 'rgba(34, 197, 94, 0.2)'
    : props.$variant === 'yellow' ? 'rgba(250, 204, 21, 0.2)'
    : 'rgba(239, 68, 68, 0.2)'
    : 'rgba(255, 255, 255, 0.03)'};
  border-color: ${props => props.$active
    ? props.$variant === 'green' ? 'rgba(34, 197, 94, 0.5)'
    : props.$variant === 'yellow' ? 'rgba(250, 204, 21, 0.5)'
    : 'rgba(239, 68, 68, 0.5)'
    : 'rgba(255, 255, 255, 0.08)'};
  color: ${props => props.$active
    ? props.$variant === 'green' ? '#4ade80'
    : props.$variant === 'yellow' ? '#facc15'
    : '#f87171'
    : '#555'};

  &:hover {
    background: ${props =>
      props.$variant === 'green' ? 'rgba(34, 197, 94, 0.15)'
    : props.$variant === 'yellow' ? 'rgba(250, 204, 21, 0.15)'
    : 'rgba(239, 68, 68, 0.15)'};
    color: ${props =>
      props.$variant === 'green' ? '#4ade80'
    : props.$variant === 'yellow' ? '#facc15'
    : '#f87171'};
  }
`;

const CommentArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 12px;
  color: #ccc;
  font-size: 13px;
  resize: vertical;
  min-height: 64px;
  outline: none;
  font-family: inherit;
  &:focus { border-color: rgba(147, 51, 234, 0.5); }
  &::placeholder { color: #555; }
`;

const SaveBtn = styled.button`
  width: 100%;
  padding: 12px;
  background: rgba(147, 51, 234, 0.2);
  border: 1px solid rgba(147, 51, 234, 0.4);
  border-radius: 10px;
  color: #c084fc;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 12px;

  &:hover { background: rgba(147, 51, 234, 0.3); }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const CategoryBadge = styled.span`
  font-size: 11px;
  color: #888;
  background: rgba(255, 255, 255, 0.05);
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 6px;
  display: inline-block;
`;

export default function QaTestDetailModal({ item, currentStatus, currentComment, onSave, onClose }) {
  const [status, setStatus] = useState(currentStatus || null);
  const [comment, setComment] = useState(currentComment || '');

  // Sync if props change (e.g. opening a different item)
  useEffect(() => {
    setStatus(currentStatus || null);
    setComment(currentComment || '');
  }, [item?.id, currentStatus, currentComment]);

  const handleSave = () => {
    if (!status) return;
    onSave(item.id, status, comment);
    onClose();
  };

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!item) return null;

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <Header>
          <Title>{item.title}</Title>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </Header>

        <Body>
          {item.category && <CategoryBadge>{item.category}</CategoryBadge>}

          {item.description && (
            <Section style={{ marginTop: 12 }}>
              <SectionLabel $color="#c084fc">
                <ClipboardList /> Mita tehdaan
              </SectionLabel>
              <SectionText>{item.description}</SectionText>
            </Section>
          )}

          {item.expected && (
            <Section>
              <SectionLabel $color="#22c55e">
                <CheckCircle2 /> Odotettu tulos
              </SectionLabel>
              <SectionText>{item.expected}</SectionText>
            </Section>
          )}

          <Divider />

          <SectionLabel>Arvio</SectionLabel>
          <TrafficRow>
            <TrafficBtn
              $variant="green"
              $active={status === 'green'}
              onClick={() => setStatus('green')}
            >
              Toimii
            </TrafficBtn>
            <TrafficBtn
              $variant="yellow"
              $active={status === 'yellow'}
              onClick={() => setStatus('yellow')}
            >
              Osittain
            </TrafficBtn>
            <TrafficBtn
              $variant="red"
              $active={status === 'red'}
              onClick={() => setStatus('red')}
            >
              Ei toimi
            </TrafficBtn>
          </TrafficRow>

          {(status === 'yellow' || status === 'red') && (
            <Section style={{ marginTop: 12 }}>
              <SectionLabel $color="#f59e0b">
                <MessageSquare /> Mika on vialla?
              </SectionLabel>
              <CommentArea
                placeholder="Kuvaile ongelma..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                autoFocus
              />
            </Section>
          )}

          {status === 'green' && (
            <Section style={{ marginTop: 12 }}>
              <CommentArea
                placeholder="Kommentti (vapaaehtoinen)..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Section>
          )}

          <SaveBtn onClick={handleSave} disabled={!status}>
            Tallenna
          </SaveBtn>
        </Body>
      </Modal>
    </Overlay>
  );
}
